import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient';

/**
 * DailyReport 簡報模式
 *
 * 用兩種 Supabase Realtime channel:
 * - lobby(每個部門一條,只用 Presence):打開 DailyReport 就自動上線,每個分頁一筆 meta。
 *   主持人把「現在看誰 / 誰在報告」放在自己的 meta 裡。Presence 會自動同步給晚加入和
 *   重連的人,所以誰掉線回來都會自己歸位,不必另外補送事件。
 * - screen(每場簡報一條,只有主持人和報告人在裡面):報告人把畫面狀態(頁籤、展開、
 *   隱藏、捲動位置)傳給主持人。資料本身不傳,主持人自己撈。
 *
 * 同一個部門可以同時有多場簡報(多個主持人各點各的人),彼此不干擾。
 */

// 閒置:10 分鐘沒在頁面上操作。只是狀態標記,連線還在,點了照樣交給他報告
export const MEMBER_IDLE_MS = 10 * 60 * 1000;
// 整場 45 分鐘沒有任何操作(主持人、報告人都沒動)就自動結束,清掉忘了關的分頁
export const HOST_IDLE_END_MS = 45 * 60 * 1000;
// 連不上超過這個時間,狀態列才給「重新整理」按鈕
export const RECONNECT_GIVE_UP_MS = 60 * 1000;
// 指定報告人之後,多久還沒收到他的畫面就提示「沒有回應」
export const PRESENTER_NO_RESPONSE_MS = 5 * 1000;

// activeAt 不必滑鼠一動就同步給所有人,20 秒一次就足夠判斷閒置
const ACTIVITY_TRACK_THROTTLE_MS = 20 * 1000;
const HOSTING_STORAGE_KEY = 'dailyReport.hosting';

const lobbyTopic = (departmentId) => `daily-report-v2:lobby:${departmentId}`;
const screenTopic = (sessionId) => `daily-report-v2:screen:${sessionId}`;

const newId = () => (
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
);

const readHostingRecord = () => {
  try { return JSON.parse(sessionStorage.getItem(HOSTING_STORAGE_KEY) || 'null'); } catch { return null; }
};
const writeHostingRecord = (record) => {
  try {
    if (record) sessionStorage.setItem(HOSTING_STORAGE_KEY, JSON.stringify(record));
    else sessionStorage.removeItem(HOSTING_STORAGE_KEY);
  } catch { /* 無痕或被封鎖時拿不到 storage,只是重新整理後不會自動回到主持 */ }
};

/** 這個分頁重新整理前是否在主持、而且還沒閒置過久(BetaLayout 用來決定要不要自動打開 DailyReport) */
export const hasResumableHosting = () => {
  const record = readHostingRecord();
  return !!record && Date.now() - (record.lastActivityAt || 0) <= HOST_IDLE_END_MS;
};

// presenceState() 是 { [tabId]: [meta, ...] }。同一個 key 可能短暫留著舊連線的那筆,取 rev 最大的
const flattenPresence = (state) => Object.values(state)
  .map((list) => list.reduce((a, b) => ((b.rev ?? 0) >= (a.rev ?? 0) ? b : a)))
  .filter((m) => m && m.userId && m.tabId);

// a 是否比 b 新:先比時間,一樣就比 tabId,確保每個分頁算出來的結果都一樣
const isNewer = (aTime, bTime, aTab, bTab) => aTime > bTime || (aTime === bTime && aTab > bTab);

// 主持人有沒有在跟著報告人的畫面(沒有 following 欄位的舊紀錄一律當作跟著)
const isFollowing = (h) => !!h?.presenterId && h.following !== false;

/**
 * 開一條「斷線會自己接回來」的 channel。
 *
 * realtime-js 在 socket 斷掉時會對所有 channel 發 CHANNEL_ERROR,之後自己重連、rejoin,
 * 成功後再發一次 SUBSCRIBED。所以 CHANNEL_ERROR / TIMED_OUT 只代表「重連中」,不能在這裡
 * 把 channel 拆掉 —— 舊版就是這樣把自己踢出簡報的。只有 server 主動關掉(收到 CLOSED,
 * 但不是我們關的)時 library 不會再 rejoin,這種情況才自己重建。
 */
function openChannel(topic, { presenceKey, bind, onSubscribed, onStatus }) {
  let channel = null;
  let closed = false;
  let everSubscribed = false;
  let rebuildTimer = null;
  let connectTimer = null;

  const build = () => {
    const config = { broadcast: { self: false } };
    if (presenceKey) config.presence = { key: presenceKey };
    const ch = supabase.channel(topic, { config });
    bind?.(ch);
    ch.subscribe((status) => {
      if (closed || ch !== channel) return;
      if (status === 'SUBSCRIBED') {
        everSubscribed = true;
        onStatus?.('connected');
        onSubscribed?.();
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        onStatus?.(everSubscribed ? 'reconnecting' : 'connecting');
      } else if (status === 'CLOSED') {
        onStatus?.('reconnecting');
        clearTimeout(rebuildTimer);
        rebuildTimer = setTimeout(() => {
          if (!closed) channel = build();
        }, 1000);
      }
    });
    // 前一條 channel 若剛好是 socket 上的最後一條,移除時 socket 會有約 100ms 處於 disconnecting,
    // 這段期間 subscribe() 內部的 connect() 會被略過 → 晚一點補一次
    clearTimeout(connectTimer);
    connectTimer = setTimeout(() => {
      if (!closed && !supabase.realtime.isConnected() && !supabase.realtime.isConnecting()) {
        supabase.realtime.connect();
      }
    }, 150);
    return ch;
  };

  channel = build();

  return {
    // 沒連上時不送:斷線期間的畫面不必補,重新 SUBSCRIBED 時會整份重送
    send: (event, payload) => {
      if (channel?.state !== 'joined') return false;
      channel.send({ type: 'broadcast', event, payload });
      return true;
    },
    track: (meta) => {
      if (channel?.state !== 'joined') return false;
      channel.track(meta);
      return true;
    },
    close: () => {
      closed = true;
      clearTimeout(rebuildTimer);
      clearTimeout(connectTimer);
      const ch = channel;
      channel = null;
      // 離開 channel 時 server 會一併移除 presence,不需要另外 untrack
      if (ch) supabase.removeChannel(ch);
    },
  };
}

/**
 * @param {Object} options
 * @param {Object} options.profile - 目前使用者
 * @param {string} options.departmentId - 所在(或主持人選擇的)部門,決定進哪個 lobby
 * @param {boolean} [options.enabled]
 * @param {Function} options.getPresenterState - 報告人:回傳要同步給主持人的畫面狀態
 * @param {Function} options.onPresenterSnapshot - 主持人:收到報告人的完整畫面狀態 (state, fromUserId)
 * @param {Function} options.onPresenterScroll - 主持人:收到報告人的捲動位置 (scrollRatio)
 */
export function useDailyReportSession({
  profile,
  departmentId,
  enabled = true,
  getPresenterState,
  onPresenterSnapshot,
  onPresenterScroll,
}) {
  const [tabId] = useState(newId);
  const myId = profile?.id ?? null;

  // 'none'(沒開 lobby)| 'connecting' | 'connected' | 'reconnecting'
  const [connection, setConnection] = useState('none');
  const [connectionSince, setConnectionSince] = useState(() => Date.now());
  const [metas, setMetas] = useState([]);
  // { departmentId, sessionId, startedAt, presenterId, following, assignedAt }
  // following:主持人是否跟著報告人的畫面。點頭像就是跟著;只想看看時可以「停止同步」,
  // 這時報告人不會再同步,他那邊會顯示「A 正在看你的內容」而不是「你正在報告中」
  const [hosting, setHosting] = useState(null);
  const [autoEnded, setAutoEnded] = useState(false);
  // 主持人端:目前同步的是報告人的哪個分頁(收到他的 snapshot 才會有)
  const [presenterTabId, setPresenterTabId] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  const connectionRef = useRef('none');
  const lobbyRef = useRef(null);
  const metasRef = useRef([]);
  const hostingRef = useRef(null);
  const hostScreenRef = useRef(null);
  const presenterScreensRef = useRef([]);
  const presenterTabRef = useRef(null);
  const activeAtRef = useRef(Date.now());
  const lastTrackedAtRef = useRef(0);
  const revRef = useRef(0);
  const sessionActivityRef = useRef(0);
  const lastPersistAtRef = useRef(0);
  const profileRef = useRef(profile);
  const getPresenterStateRef = useRef(getPresenterState);
  const onPresenterSnapshotRef = useRef(onPresenterSnapshot);
  const onPresenterScrollRef = useRef(onPresenterScroll);

  useEffect(() => { profileRef.current = profile; }, [profile]);
  useEffect(() => { getPresenterStateRef.current = getPresenterState; }, [getPresenterState]);
  useEffect(() => { onPresenterSnapshotRef.current = onPresenterSnapshot; }, [onPresenterSnapshot]);
  useEffect(() => { onPresenterScrollRef.current = onPresenterScroll; }, [onPresenterScroll]);

  const updateConnection = useCallback((status) => {
    if (connectionRef.current === status) return;
    connectionRef.current = status;
    setConnection(status);
    setConnectionSince(Date.now());
  }, []);

  // 把目前狀態送進 lobby presence。沒連上時不送,SUBSCRIBED 時會整份補上
  const pushMeta = useCallback(() => {
    const p = profileRef.current;
    const lobby = lobbyRef.current;
    if (!p || !lobby) return;
    const h = hostingRef.current;
    const sent = lobby.track({
      userId: p.id,
      name: p.name,
      avatar_url: p.avatar_url,
      tabId,
      activeAt: activeAtRef.current,
      host: h
        ? { sessionId: h.sessionId, startedAt: h.startedAt, presenterId: h.presenterId, following: isFollowing(h) }
        : null,
      rev: ++revRef.current,
    });
    if (sent) lastTrackedAtRef.current = Date.now();
  }, [tabId]);

  // 主持狀態寫進 sessionStorage,重新整理後才能接著主持
  const persistHosting = useCallback((force = false) => {
    const h = hostingRef.current;
    if (!h) { writeHostingRecord(null); return; }
    const t = Date.now();
    if (!force && t - lastPersistAtRef.current < 5000) return;
    lastPersistAtRef.current = t;
    writeHostingRecord({ ...h, lastActivityAt: sessionActivityRef.current });
  }, []);

  const touchSession = useCallback(() => {
    if (!hostingRef.current) return;
    sessionActivityRef.current = Date.now();
    persistHosting();
  }, [persistHosting]);

  const applyHosting = useCallback((next, { autoEnded: ended = false } = {}) => {
    hostingRef.current = next;
    setHosting(next);
    presenterTabRef.current = null;
    setPresenterTabId(null);
    setAutoEnded(!next && ended);
    persistHosting(true);
    pushMeta();
  }, [persistHosting, pushMeta]);

  // 整場閒置過久 → 結束。一律用時間戳判斷:電腦睡一晚醒來,timer 沒跑也算得出來
  const checkHostExpired = useCallback(() => {
    if (!hostingRef.current) return false;
    if (Date.now() - sessionActivityRef.current <= HOST_IDLE_END_MS) return false;
    applyHosting(null, { autoEnded: true });
    return true;
  }, [applyHosting]);

  // ── 主持人操作 ──

  const startHosting = useCallback(() => {
    if (!departmentId) return;
    // 同一個人的其他分頁正在主持 → 沿用那一場的報告人,換分頁接手時畫面不會斷
    const mine = metasRef.current.filter((m) => m.userId === myId && m.tabId !== tabId && m.host);
    const latest = mine.reduce(
      (a, b) => (!a || isNewer(b.host.startedAt, a.host.startedAt, b.tabId, a.tabId) ? b : a),
      null,
    );
    const t = Date.now();
    sessionActivityRef.current = t;
    applyHosting({
      departmentId,
      sessionId: newId(),
      startedAt: t,
      presenterId: latest?.host.presenterId ?? null,
      following: latest ? latest.host.following !== false : true,
      assignedAt: t,
    });
  }, [departmentId, myId, tabId, applyHosting]);

  const endHosting = useCallback(() => applyHosting(null), [applyHosting]);

  const dismissAutoEnded = useCallback(() => setAutoEnded(false), []);

  /**
   * 主持人切換要看的人。不是自己就同時交給他報告(跟著他的畫面);他離線也照樣看得到內容,只是不同步。
   * 停止同步之後再點一次同一個人 = 恢復同步
   */
  const assignPresenter = useCallback((userId) => {
    const h = hostingRef.current;
    if (!h) return;
    const t = Date.now();
    sessionActivityRef.current = t;
    applyHosting({ ...h, presenterId: userId && userId !== myId ? userId : null, following: true, assignedAt: t });
    // 對方可能本來就在 screen channel 上 → 跟他要一份最新畫面
    hostScreenRef.current?.send('request_snapshot', {});
  }, [myId, applyHosting]);

  /** 只想看看、不想被報告人的捲動影響時停止同步;恢復時重新跟著他的畫面 */
  const setFollowing = useCallback((following) => {
    const h = hostingRef.current;
    if (!h?.presenterId) return;
    const t = Date.now();
    sessionActivityRef.current = t;
    applyHosting({ ...h, following, assignedAt: t });
    if (following) hostScreenRef.current?.send('request_snapshot', {});
  }, [applyHosting]);

  // ── 活動偵測(閒置判斷、主持閒置計時)──

  const markActivity = useCallback((immediate = false) => {
    // 先檢查再更新:睡醒後的第一個滑鼠事件不能把已經過期的主持又續命
    checkHostExpired();
    const t = Date.now();
    activeAtRef.current = t;
    touchSession();
    const since = t - lastTrackedAtRef.current;
    if (since >= ACTIVITY_TRACK_THROTTLE_MS || (immediate && since >= 3000)) pushMeta();
  }, [checkHostExpired, touchSession, pushMeta]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onActivity = () => markActivity(false);
    const onBack = () => {
      if (document.visibilityState !== 'visible') return;
      // 切回分頁時連線若已經斷了就馬上重連,不等 backoff
      if (!supabase.realtime.isConnected() && !supabase.realtime.isConnecting()
        && supabase.realtime.getChannels().length > 0) {
        supabase.realtime.connect();
      }
      markActivity(true);
    };
    const opts = { capture: true, passive: true };
    const events = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, onActivity, opts));
    document.addEventListener('visibilitychange', onBack);
    window.addEventListener('focus', onBack);
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity, opts));
      document.removeEventListener('visibilitychange', onBack);
      window.removeEventListener('focus', onBack);
    };
  }, [enabled, markActivity]);

  // ── lobby:打開 DailyReport 就上線 ──

  useEffect(() => {
    if (!enabled || !myId || !departmentId) return undefined;

    if (hostingRef.current && hostingRef.current.departmentId !== departmentId) {
      // 換了部門 → 原本那場結束
      applyHosting(null);
    } else if (!hostingRef.current) {
      // 重新整理前在主持而且還沒閒置過久 → 接著主持。startedAt 沿用,
      // 如果同一個人已經在別的分頁開了更新的一場,這邊會自己退下
      const record = readHostingRecord();
      if (record && record.departmentId === departmentId
        && Date.now() - (record.lastActivityAt || 0) <= HOST_IDLE_END_MS) {
        const { lastActivityAt, ...rest } = record;
        const resumed = { ...rest, assignedAt: Date.now() };
        sessionActivityRef.current = lastActivityAt;
        hostingRef.current = resumed;
        setHosting(resumed);
      } else if (record) {
        writeHostingRecord(null);
      }
    }

    updateConnection('connecting');
    const lobby = openChannel(lobbyTopic(departmentId), {
      presenceKey: tabId,
      bind: (ch) => {
        ch.on('presence', { event: 'sync' }, () => {
          const list = flattenPresence(ch.presenceState());
          metasRef.current = list;
          setMetas(list);
        });
      },
      onSubscribed: () => {
        // 電腦睡醒重連時先檢查主持是不是已經閒置過久,過期就直接結束,不會又冒出來當主持人
        checkHostExpired();
        pushMeta();
      },
      onStatus: updateConnection,
    });
    lobbyRef.current = lobby;

    return () => {
      lobbyRef.current = null;
      lobby.close();
      metasRef.current = [];
      setMetas([]);
      updateConnection('none');
    };
  }, [enabled, myId, departmentId, tabId, applyHosting, checkHostExpired, pushMeta, updateConnection]);

  // ── 主持人:自己這場的 screen channel,接收報告人的畫面 ──

  const hostSessionId = hosting?.sessionId ?? null;
  useEffect(() => {
    if (!enabled || !hostSessionId) return undefined;
    const screen = openChannel(screenTopic(hostSessionId), {
      bind: (ch) => {
        ch.on('broadcast', { event: 'snapshot' }, ({ payload }) => {
          const h = hostingRef.current;
          if (!isFollowing(h) || !payload || payload.fromUserId !== h.presenterId) return;
          touchSession();
          presenterTabRef.current = payload.tabId;
          setPresenterTabId(payload.tabId);
          onPresenterSnapshotRef.current?.(payload.state, payload.fromUserId);
        });
        ch.on('broadcast', { event: 'scroll' }, ({ payload }) => {
          const h = hostingRef.current;
          if (!isFollowing(h) || !payload || payload.fromUserId !== h.presenterId) return;
          // 報告人換分頁時,以新分頁送來的 snapshot 為準,舊分頁殘留的捲動不理
          if (payload.tabId !== presenterTabRef.current) return;
          touchSession();
          onPresenterScrollRef.current?.(payload.scrollRatio);
        });
      },
      // 剛連上(或重連)時跟報告人要一份完整畫面
      onSubscribed: () => screen.send('request_snapshot', {}),
    });
    hostScreenRef.current = screen;
    return () => {
      hostScreenRef.current = null;
      screen.close();
    };
  }, [enabled, hostSessionId, touchSession]);

  // 正在同步的那個分頁不見了(關掉或斷線),或它自己開始主持(主持中的分頁不負責報告)
  // → 回到等待狀態;他還有別的分頁的話,那個分頁會接著送畫面過來
  useEffect(() => {
    const tab = presenterTabRef.current;
    if (!tab) return;
    const meta = metas.find((m) => m.tabId === tab);
    if (!meta || meta.host) {
      presenterTabRef.current = null;
      setPresenterTabId(null);
    }
  }, [metas]);

  // 同一個人在別的分頁(或別台電腦)開了更新的一場 → 這邊退下
  useEffect(() => {
    const h = hostingRef.current;
    if (!h || !myId) return;
    const newer = metas.some((m) => m.userId === myId && m.tabId !== tabId && m.host
      && isNewer(m.host.startedAt, h.startedAt, m.tabId, tabId));
    if (newer) applyHosting(null);
  }, [metas, myId, tabId, applyHosting]);

  // ── 報告人:被哪幾場指定、由自己哪個分頁負責 ──

  const assignments = useMemo(() => {
    if (!myId) return [];
    const bySession = new Map();
    metas.forEach((m) => {
      if (m.userId !== myId && m.host?.presenterId === myId && !bySession.has(m.host.sessionId)) {
        bySession.set(m.host.sessionId, {
          sessionId: m.host.sessionId,
          hostUserId: m.userId,
          hostName: m.name,
          following: m.host.following !== false,
        });
      }
    });
    return [...bySession.values()];
  }, [metas, myId]);

  // 同一個人開了好幾個分頁 → 交給最近在用的那個(正在主持的分頁不負責報告)
  const isElectedTab = useMemo(() => {
    const mine = metas.filter((m) => m.userId === myId && !m.host);
    if (!mine.some((m) => m.tabId === tabId)) return false;
    const best = mine.reduce((a, b) => (isNewer(b.activeAt ?? 0, a.activeAt ?? 0, b.tabId, a.tabId) ? b : a));
    return best.tabId === tabId;
  }, [metas, myId, tabId]);

  // 只有主持人跟著的場次才同步;停止同步的主持人只是在看我的內容
  const followedBy = useMemo(() => assignments.filter((a) => a.following), [assignments]);
  const viewedBy = useMemo(
    () => (hosting ? [] : assignments.filter((a) => !a.following)),
    [hosting, assignments],
  );
  const presentingTo = useMemo(
    () => (isElectedTab && !hosting ? followedBy : []),
    [isElectedTab, hosting, followedBy],
  );
  const presentingElsewhere = !isElectedTab && !hosting && followedBy.length > 0;

  const sendSnapshot = useCallback(() => {
    const chans = presenterScreensRef.current;
    if (!chans.length || !myId) return;
    const state = getPresenterStateRef.current?.();
    if (!state) return;
    const payload = { fromUserId: myId, tabId, state };
    chans.forEach((c) => c.send('snapshot', payload));
  }, [myId, tabId]);

  const publishScroll = useCallback((scrollRatio) => {
    if (!myId) return;
    presenterScreensRef.current.forEach((c) => c.send('scroll', { fromUserId: myId, tabId, scrollRatio }));
  }, [myId, tabId]);

  const presentKey = presentingTo.map((a) => a.sessionId).sort().join(',');
  useEffect(() => {
    if (!enabled || !presentKey) return undefined;
    const chans = presentKey.split(',').map((sessionId) => openChannel(screenTopic(sessionId), {
      bind: (ch) => {
        ch.on('broadcast', { event: 'request_snapshot' }, () => sendSnapshot());
      },
      onSubscribed: () => sendSnapshot(),
    }));
    presenterScreensRef.current = chans;
    return () => {
      presenterScreensRef.current = [];
      chans.forEach((c) => c.close());
    };
  }, [enabled, presentKey, sendSnapshot]);

  // ── 狀態列用的計時 ──
  // 剛斷線(還沒到放棄門檻)或剛指定報告人(還沒到「沒有回應」門檻)時每秒更新,
  // 過了門檻狀態就不會再變,改回主持中 30 秒一次(更新閒置狀態、檢查整場閒置)

  const presenterOnline = !!hosting?.presenterId && metas.some((m) => m.userId === hosting.presenterId);
  // 報告人自己也開始主持了,而且沒有其他分頁可以接著報告 → 他的畫面不會再同步過來
  const presenterHosting = presenterOnline
    && metas.filter((m) => m.userId === hosting.presenterId).every((m) => m.host);
  const following = isFollowing(hosting);
  const isConnecting = connection === 'connecting' || connection === 'reconnecting';
  const needFastTick = (isConnecting && now - connectionSince <= RECONNECT_GIVE_UP_MS)
    || (following && presenterOnline && !presenterHosting && !presenterTabId
      && now - (hosting?.assignedAt ?? 0) <= PRESENTER_NO_RESPONSE_MS);
  const isHosting = !!hosting;
  useEffect(() => {
    if (!enabled || (!needFastTick && !isHosting)) return undefined;
    setNow(Date.now());
    const id = setInterval(() => {
      setNow(Date.now());
      checkHostExpired();
    }, needFastTick ? 1000 : 30 * 1000);
    return () => clearInterval(id);
  }, [enabled, needFastTick, isHosting, checkHostExpired]);

  // ── 成員上線狀態 ──

  const lastActiveByUser = useMemo(() => {
    const map = new Map();
    metas.forEach((m) => map.set(m.userId, Math.max(map.get(m.userId) ?? 0, m.activeAt ?? 0)));
    return map;
  }, [metas]);

  /** 'online' | 'idle' | 'offline' */
  const getMemberStatus = useCallback((userId) => {
    if (!userId) return 'offline';
    if (userId === myId) return 'online';
    if (!lastActiveByUser.has(userId)) return 'offline';
    return now - lastActiveByUser.get(userId) > MEMBER_IDLE_MS ? 'idle' : 'online';
  }, [lastActiveByUser, myId, now]);

  const anotherTabHosting = !hosting && metas.some((m) => m.userId === myId && m.tabId !== tabId && m.host);

  return {
    connection,
    connectionSince,
    now,
    hosting,
    following,
    autoEnded,
    presenterTabId,
    presenterOnline,
    presenterHosting,
    anotherTabHosting,
    presentingTo,
    presentingElsewhere,
    viewedBy,
    getMemberStatus,
    startHosting,
    endHosting,
    dismissAutoEnded,
    assignPresenter,
    setFollowing,
    publishState: sendSnapshot,
    publishScroll,
  };
}
